import {
  TextField,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { ContentDiv, RowDiv } from "../styles";
import { CoordinatorItem } from "../CoordinatorItem";
import { FaCrown, FaPlus } from "react-icons/fa";
import { StyledP } from "../styles";
import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { useAllUsers } from "../../../../queries/hooks";
import { LabeledAutoComplete } from "../../../UI/LabeledAutoComplete";

interface IProps {
  data: any;
  onChange: (event: any) => void;
}

export function RegisterStep({ data, onChange }: IProps) {
  const apiData = data[1] || data;
  const workingData = { ...apiData };

  if (!workingData.cell_admins_ids) {
    workingData.cell_admins_ids = [];
  }

  const {
    data: usersData,
    error: userError,
    isLoading,
  } = useAllUsers();
  const [leaderSearchTerm, setLeaderSearchTerm] = useState("");
  const [leaders, setLeaders] = useState<any>([]);
  const [showTooltip, setShowToolTip] = useState(false);
  const [showAdminTooltip, setShowAdminTooltip] = useState(false);
  const [coordinatorSearchTerm, setCoordinatorSearchTerm] = useState("");
  const [adminSearchTerm, setAdminSearchTerm] = useState("");
  const [admins, setAdmins] = useState<any>([]);
  const [adminEmails, setAdminEmails] = useState<string[]>([]);

  const handleChangeLeaders = (
    event: SyntheticEvent<Element, Event>,
    newValue: any
  ) => {
    let e = {
      target: {
        name: "leader_user_ids",
        value: newValue.map((el: any) => el._id),
      },
    };
    setLeaders(newValue);
    onChange(e);
    setLeaderSearchTerm("");
  };

  const handleChangeCoordinators = (
    event: SyntheticEvent<Element, Event>,
    newValue: any
  ) => {
    let e = {
      target: {
        name: "coordinators_user_ids",
        value: newValue.map((el: any) => el._id),
      },
    };
    onChange(e);
    setCoordinatorSearchTerm("");
  };

  const handleChangeAdmins = (
    event: SyntheticEvent<Element, Event>,
    newValue: any
  ) => {
    let e = {
      target: {
        name: "cell_admins_ids",
        value: newValue.map((el: any) => el._id),
      },
    };
    setAdmins(newValue);
    onChange(e);
    setAdminSearchTerm("");
  };

  const handleChangeAdminEmails = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    const emails = inputValue
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);

    setAdminEmails(inputValue.split(",").map((email) => email.trim())); // Keep the raw input for display
    let e = {
      target: {
        name: "admin_emails",
        value: emails, // Send only non-empty emails to parent
      },
    };

    onChange(e);
  };

  const showChangeLeader = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setShowToolTip(true);
  };

  const setNewLeader = (tagValue: any[], option: any) => {
    const leadersArr = tagValue.filter((el: any) => el._id !== option._id);
    leadersArr.unshift(option);
    setLeaders(leadersArr);
    let e = {
      target: {
        name: "leader_user_ids",
        value: leadersArr.map((el: any) => el._id),
      },
    };
    onChange(e);
  };

  useEffect(() => {
    if (data.leader_user_ids?.toString() === "") return;
    let leaderArr = data.leader_user_ids?.map((el: any) => {
      const user = usersData?.find((user) => user._id === el);
      if (user) return user;
      else return null;
    });
    setLeaders(leaderArr || []);
  }, [usersData]);

  useEffect(() => {
    const cellAdmins = data && data[1] && data[1].cell_admins_ids;

    if (cellAdmins && Array.isArray(cellAdmins)) {
      workingData.cell_admins_ids = [...cellAdmins];
    } else if (data.cell_admins_ids && Array.isArray(data.cell_admins_ids)) {
      workingData.cell_admins_ids = [...data.cell_admins_ids];
    }
  }, [data]);

  const coordinatorValues =
    usersData?.filter((el: any) =>
      data.coordinators_user_ids?.includes(el._id)
    ) || [];

  const adminValues =
    usersData?.filter((el: any) =>
      workingData.cell_admins_ids?.includes(el._id)
    ) || [];

  useEffect(() => {
    setAdmins(adminValues);
  }, [adminValues]);

  useEffect(() => {
    // Handle both data structure patterns like other fields
    const emails = (data[1] && data[1].admin_emails) || data.admin_emails || [];
    setAdminEmails(Array.isArray(emails) ? emails : []);
  }, [data]);

  return (
    <>
      <ContentDiv>
        <RowDiv>
          <InputLabel>Nome da célula</InputLabel>
          <TextField
            value={data.name}
            onChange={onChange}
            size="small"
            name="name"
          />
        </RowDiv>
        <RowDiv>
          <InputLabel>Descrição</InputLabel>
          <TextField
            value={data.description}
            onChange={onChange}
            size="small"
            multiline
            name="description"
          />
        </RowDiv>
        <RowDiv></RowDiv>
      </ContentDiv>
      <div
        style={{
          width: "100%",
          height: 1,
          backgroundColor: "#E0E0E0",
          marginTop: 16,
          marginBottom: 32,
        }}
      />
      <ContentDiv style={{ width: "100%" }}>
        <LabeledAutoComplete
          multiple
          options={usersData || []}
          inputValue={leaderSearchTerm}
          value={leaders}
          disablePortal
          getOptionLabel={(option) => option?.name || ""}
          size="small"
          label="Líder da Célula"
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                onChange={(
                  e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                ) => {
                  setLeaderSearchTerm(e.target.value);
                }}
              />
            );
          }}
          renderTags={(tagValue, getTagProps) => {
            return tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                label={
                  <span style={{ alignItems: "center" }}>
                    {index === 0 ? <FaCrown /> : ""} {option?.name || ""}
                  </span>
                }
                onClick={(e) => setNewLeader(tagValue, option)}
                onMouseEnter={showChangeLeader}
                onMouseLeave={() => setShowToolTip(false)}
              />
            ));
          }}
          onChange={handleChangeLeaders}
        />
        {!!leaders.length && (
          <div style={{ height: "10px", marginBottom: 24 }}>
            <span style={{ color: "#666666", fontSize: 13 }}>
              Clique no usuário para definir o líder.
            </span>
          </div>
        )}
      </ContentDiv>
      <ContentDiv style={{ width: "100%" }}>
        <LabeledAutoComplete
          multiple
          options={usersData || []}
          inputValue={coordinatorSearchTerm}
          value={coordinatorValues}
          disablePortal
          getOptionLabel={(option) => option.name || ""}
          size="small"
          label="Coordenadores da Célula"
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                onChange={(
                  e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                ) => {
                  setCoordinatorSearchTerm(e.target.value);
                }}
              />
            );
          }}
          onChange={handleChangeCoordinators}
        />
      </ContentDiv>
      <ContentDiv style={{ width: "100%" }}>
        <LabeledAutoComplete
          multiple
          options={usersData || []}
          inputValue={adminSearchTerm}
          value={admins}
          disablePortal
          getOptionLabel={(option) => option?.name || ""}
          size="small"
          label="ADM da Célula"
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                onChange={(
                  e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                ) => {
                  setAdminSearchTerm(e.target.value);
                }}
              />
            );
          }}
          onChange={handleChangeAdmins}
        />
        <div style={{ color: "#666666", fontSize: 13, marginTop: 4 }}>
          Os usuários adicionados neste campo terão acesso a todos os projetos
          da célula, independente de estarem atribuídos diretamente a eles.
        </div>
      </ContentDiv>
      <ContentDiv style={{ width: "100%" }}>
        <InputLabel>Email de Grupo ADM's</InputLabel>
        <TextField
          value={Array.isArray(adminEmails) ? adminEmails.join(", ") : ""}
          onChange={handleChangeAdminEmails}
          size="small"
          name="admin_emails"
          placeholder="seuemail@exemplo.com"
          fullWidth
          multiline
          rows={2}
        />
        <div style={{ color: "#666666", fontSize: 13, marginTop: 4 }}>
          Adicione emails separados por vírgula para notificações do grupo ADM.
        </div>
      </ContentDiv>
    </>
  );
}
